import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  FormControl,
  Select,
  MenuItem,
  Button,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useStores } from "../../../use-stores";
import { useDebounce } from "../common/hooks/Debounce";
import {
  PackProduct,
  PackProductVariant,
  Swagpack,
} from "../../../models/Shipments";
import NumericTextField from "../common/pop-up/NumericTextField";
import FormTextField from "../common/form/FormTextField";
import CustomTypography from "../common/form/CustomTypography";
import { observer } from "mobx-react";
import { GetPacksParams } from "../../../stores/ShipmentStore";

interface AddPacksProps {
  open: boolean;
  getPacksParams: GetPacksParams;
  onClose: () => void;
  onSavePacks: (selectedPacks: any) => void;
}

interface SelectedPack extends Swagpack {
  uniqueId: string;
  quantity: number | string;
}

type PackTab = "available" | "consumed" | "all";

const AddPacksPopup: React.FC<AddPacksProps> = ({
  open,
  getPacksParams,
  onClose,
  onSavePacks,
}) => {
  const { shipmentStore } = useStores();
  const [selectedPacks, setSelectedPacks] = useState<SelectedPack[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [packTab, setPackTab] = useState<PackTab>("available");
  const debouncedSearchText = useDebounce(searchText, 400);
  const lastFetchedSearch = useRef<string | null>(null);

  const filteredPacks = useMemo(() => {
    const packs = shipmentStore?.currentPacks || [];
    if (packTab !== "all") return packs;
    return [...packs].sort((a: Swagpack, b: Swagpack) => {
      const availableA =
        Number(a.quantity) -
        Number(a.used_quantity) -
        Number(a.blocked_quantity || 0);
      const availableB =
        Number(b.quantity) -
        Number(b.used_quantity) -
        Number(b.blocked_quantity || 0);
      if (availableB !== availableA) return availableB - availableA;
      return (a.label || "").localeCompare(b.label || "");
    });
  }, [shipmentStore?.currentPacks, packTab]);

  useEffect(() => {
    if (!open || !getPacksParams?.shipment_ids?.length) return;
    if (
      debouncedSearchText.trim() !== "" &&
      debouncedSearchText.trim().length < 3
    ) {
      return;
    }

    const cacheKey = `${debouncedSearchText}-${packTab}`;
    if (lastFetchedSearch.current === cacheKey) return;
    lastFetchedSearch.current = cacheKey;

    shipmentStore.getShipmentPacks({
      ...getPacksParams,
      search_text: debouncedSearchText.trim() || undefined,
      available_only: packTab === "available" ? true : undefined,
      consumed_only: packTab === "consumed" ? true : undefined,
    });
  }, [open, getPacksParams, debouncedSearchText, packTab]);

  useEffect(() => {
    if (!open) {
      setSearchText("");
      setPackTab("available");
      lastFetchedSearch.current = null;
    }
  }, [open]);

  const generateUniqueId = () => {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  const handleAddPack = (pack: Swagpack) => {
    const resetProducts = pack.products.map((product) => ({
      ...product,
      selectedVariant: [],
    }));

    const uniquePack = {
      ...pack,
      uniqueId: generateUniqueId(),
      quantity: 1,
      products: resetProducts,
    };
    setSelectedPacks((prev) => [...prev, uniquePack]);
  };

  const handleRemovePack = (uniqueId: string) => {
    setSelectedPacks((prev) =>
      prev.filter((pack) => pack.uniqueId !== uniqueId),
    );
  };

  const handleVariantChange = (
    packIndex: number,
    productIndex: number,
    variantIndex: number,
    selectedVariantId: any,
  ) => {
    setSelectedPacks((prev) => {
      const updatedPacks = [...prev];
      const selectedProduct = updatedPacks[packIndex].products[productIndex];

      const selectedVariant = selectedProduct?.variants?.find(
        (variant) => variant.id === selectedVariantId,
      );

      if (!selectedProduct.selectedVariant) {
        selectedProduct.selectedVariant = [];
      }

      if (selectedVariant) {
        selectedProduct.selectedVariant[variantIndex] = {
          ...selectedVariant,
          size: selectedVariant.size,
          color: selectedVariant.color || undefined,
        };
      }

      return updatedPacks;
    });
  };

  const handleQuantityChange = (uniqueId: string, value: number | string) => {
    setSelectedPacks((prev) => {
      const updatedPacks = prev.map((pack: SelectedPack) => {
        if (pack.uniqueId === uniqueId) {
          return { ...pack, quantity: value };
        }
        return pack;
      });
      return updatedPacks;
    });
  };

  const validatePacks = () => {
    for (const pack of selectedPacks) {
      if (!pack.quantity || (pack.quantity as number) <= 0) {
        setErrorMessage("Please ensure all packs have a valid quantity.");
        return false;
      }

      for (const product of pack.products) {
        const { quantity, selectedVariant } = product;

        if (!quantity || quantity <= 0) {
          setErrorMessage("Please ensure all products have a valid quantity.");
          return false;
        }

        if (!selectedVariant || selectedVariant.length !== quantity) {
          setErrorMessage(`Please ensure all variants are selected`);
          return false;
        }

        for (let i = 0; i < quantity; i++) {
          if (!selectedVariant[i] || !selectedVariant[i].id) {
            setErrorMessage(
              `Please select a variant for every dropdown in the product`,
            );
            return false;
          }
        }
      }
    }
    setErrorMessage("");
    return true;
  };

  const handleSave = () => {
    if (!validatePacks()) {
      return;
    }

    const formattedData = {
      shipment_packs: selectedPacks.map((pack) => ({
        id: pack.id,
        quantity: pack.quantity || 1,
        inventory_id: pack.inventory_id,
        products: pack.products.map((product: PackProduct) => {
          const variantMap: {
            [id: string]: {
              id: number;
              size: string;
              color: string | null;
              quantity: number;
            };
          } = {};
          product.selectedVariant?.forEach((variant: PackProductVariant) => {
            if (variantMap[variant.id]) {
              variantMap[variant.id].quantity += 1;
            } else {
              variantMap[variant.id] = {
                id: variant.id,
                size: variant.size,
                color: variant.color || null,
                quantity: 1,
              };
            }
          });
          const uniqueVariants = Object.values(variantMap);
          return {
            product_id: product.product_id,
            variants: uniqueVariants,
          };
        }),
      })),
    };
    onSavePacks(formattedData.shipment_packs);
    onClose();
  };

  return (
    <Modal
      open={open}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 2, md: 0 },
      }}
    >
      <Box
        sx={{
          padding: { xs: 2, md: 3 },
          backgroundColor: "background.paper",
          width: { xs: "100%", sm: "95%", md: "85%", lg: "70%" },
          maxWidth: 900,
          minHeight: 520,
          borderRadius: 2,
          boxShadow:
            "0 24px 48px -12px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          maxHeight: { xs: "90vh", md: "85vh" },
          overflowY: "auto",
          outline: "none",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomTypography
          variant="heading6"
          fontWeight={600}
          sx={{ mb: 2, color: "text.primary" }}
        >
          Select Swag for the address
        </CustomTypography>

        <Box
          className="modal-content"
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 3 },
            flex: 1,
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <Box
            className="pack-list"
            sx={{
              flex: { xs: "none", md: 1 },
              minWidth: { xs: "100%", md: "280px" },
              maxHeight: { xs: "45vh", md: "60vh" },
              overflowY: "auto",
              borderRight: { xs: 0, md: 1 },
              borderBottom: { xs: 1, md: 0 },
              borderColor: "divider",
              pr: { xs: 0, md: 2 },
              pb: { xs: 2, md: 0 },
            }}
          >
            <FormTextField
              placeholder="Search by name or SKU"
              value={searchText}
              onChange={(_id, value) => setSearchText(value || "")}
              sx={{ mb: 1 }}
            />
            <Tabs
              value={packTab}
              onChange={(_, v: PackTab) => setPackTab(v)}
              sx={{
                minHeight: 36,
                "& .MuiTab-root": {
                  minHeight: 36,
                  py: 0.5,
                  fontSize: "0.8rem",
                },
                "& .MuiTabs-indicator": {
                  height: 2,
                  borderRadius: "2px 2px 0 0",
                },
              }}
            >
              <Tab label="Available" value="available" />
              <Tab label="Consumed" value="consumed" />
              <Tab label="All" value="all" />
            </Tabs>
            {filteredPacks.map((pack: Swagpack) => (
              <Box
                key={pack.id}
                className="pack-item"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  py: 1.5,
                  px: 1,
                  mb: 0.5,
                  borderRadius: 1,
                  "&:hover": { backgroundColor: "action.hover" },
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  sx={{ flex: 1, fontSize: "0.85rem" }}
                >
                  {pack.label}
                </CustomTypography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CustomTypography
                    variant="body2"
                    fontWeight={600}
                    sx={{ fontSize: "0.85rem", color: "text.secondary" }}
                  >
                    {`${
                      Number(pack.quantity) -
                      Number(pack.used_quantity) -
                      Number(pack.blocked_quantity || 0)
                    }`}
                  </CustomTypography>
                  <IconButton
                    onClick={() => handleAddPack(pack)}
                    color="primary"
                    size="small"
                    sx={{
                      "&:hover": {
                        backgroundColor: "primary.main",
                        color: "primary.contrastText",
                      },
                    }}
                  >
                    <AddIcon sx={{ fontSize: "1rem" }} />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>

          <Box
            className="selected-packs"
            sx={{
              flex: { xs: 1, md: 4 },
              overflow: "auto",
              maxHeight: { xs: "45vh", md: "60vh" },
              borderRadius: 1,
              backgroundColor: { xs: "action.hover", md: "transparent" },
            }}
          >
            {selectedPacks.map((pack, packIndex) => (
              <Box
                key={pack.uniqueId}
                sx={{
                  position: "relative",
                  mb: 2,
                  p: 2,
                  borderRadius: 1.5,
                  boxShadow:
                    "0 2px 8px rgba(0,0,0,0.08), 0 0 1px rgba(0,0,0,0.06)",
                  backgroundColor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <IconButton
                  sx={{
                    color: "grey.600",
                    position: "absolute",
                    top: 4,
                    right: 4,
                    backgroundColor: "action.hover",
                    borderRadius: "50%",
                    "&:hover": {
                      backgroundColor: "action.selected",
                    },
                  }}
                  onClick={() => handleRemovePack(pack.uniqueId)}
                  size="small"
                >
                  <CloseIcon sx={{ fontSize: "14px" }} />
                </IconButton>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                    paddingTop: 1,
                    paddingRight: 2,
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{ fontSize: "14px", fontWeight: 600 }}
                  >
                    {pack.label}
                  </Typography>
                  <NumericTextField
                    sx={{
                      "& .MuiInputBase-input": {
                        fontSize: "12px",
                        width: "60px",
                        textAlign: "center",
                      },
                    }}
                    type="number"
                    placeholder="Qty"
                    size="small"
                    value={pack.quantity}
                    onChange={(e) =>
                      handleQuantityChange(
                        pack.uniqueId,
                        e.target.value === "" ? "" : parseInt(e.target.value),
                      )
                    }
                  />
                </Box>

                <Box
                  className="product-list"
                  sx={{
                    display: "flex",
                    gap: 2,
                    overflowX: "auto",
                    overflowY: "hidden",
                    pb: 1,
                  }}
                >
                  {pack?.products?.map(
                    (product: PackProduct, productIndex: number) => (
                      <Box
                        key={product.product_id}
                        sx={{ width: "100px", textAlign: "center" }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100px",
                            height: "100px",
                            p: 2,
                            backgroundColor: "rgba(246, 245, 255, 1)",
                            mb: 1,
                            borderRadius: 1,
                          }}
                        >
                          <img
                            src={product?.images || ""}
                            alt={product.product_name}
                            style={{
                              aspectRatio: "1/1",
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </Box>

                        <Tooltip title={product.product_name} placement="top">
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: "10px",
                              textAlign: "left",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              maxWidth: "100px",
                            }}
                          >
                            {product.product_name}
                          </Typography>
                        </Tooltip>

                        {[...Array(product.quantity)].map((_, variantIndex) => {
                          if (product!.variants?.length === 1) {
                            const singleVariant = product.variants[0];
                            if (
                              !product.selectedVariant ||
                              !product.selectedVariant[variantIndex] ||
                              product.selectedVariant[variantIndex].id !==
                                singleVariant.id
                            ) {
                              handleVariantChange(
                                packIndex,
                                productIndex,
                                variantIndex,
                                singleVariant.id,
                              );
                            }
                          }

                          return (
                            <FormControl
                              fullWidth
                              size="small"
                              key={variantIndex}
                              sx={{ mt: 1 }}
                            >
                              <Select
                                sx={{ height: "20px", fontSize: "12px" }}
                                value={
                                  product.selectedVariant &&
                                  product.selectedVariant[variantIndex]
                                    ? product.selectedVariant[variantIndex].id
                                    : ""
                                }
                                onChange={(e) =>
                                  handleVariantChange(
                                    packIndex,
                                    productIndex,
                                    variantIndex,
                                    e.target.value,
                                  )
                                }
                              >
                                {product?.variants?.map(
                                  (variant: PackProductVariant) => {
                                    let displayText = variant.size;
                                    if (variant.color) {
                                      displayText += ` - ${variant.color}`;
                                    }
                                    return (
                                      <MenuItem
                                        key={variant.id}
                                        value={variant.id}
                                        sx={{ fontSize: "12px" }}
                                      >
                                        {displayText}
                                      </MenuItem>
                                    );
                                  },
                                )}
                              </Select>
                            </FormControl>
                          );
                        })}
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        {errorMessage && (
          <Typography
            color="error"
            sx={{ mt: 2, textAlign: "center", fontSize: "0.875rem" }}
          >
            {errorMessage}
          </Typography>
        )}
        <Box
          className="modal-actions"
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 1.5,
            mt: 2,
            pt: 2,
            borderTop: 1,
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => onClose()}
            sx={{ px: { xs: 2, md: 3 } }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            sx={{ px: { xs: 2, md: 3 } }}
            disabled={selectedPacks.length === 0}
          >
            Save Details
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default observer(AddPacksPopup);
